import moment from 'moment'
import { buildBookingTitleGenderMap } from 'checkout/lib/utils/cruises/booking'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { createSelector } from 'reselect'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { deduceGenderFromTitle, getPackageOptionType } from 'lib/tours/tourUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import {
  isAccommodationItem,
  findPostPurchaseCheckout,
  getConnectRoomsSpecialRequestMessage,
  isBedbankItem,
  isBNBLLEHotelItem,
  isCarHireItem,
  isCruiseItem,
  isFlightItem,
  isInstantBookingLEHotelItem,
  isTourV1Item,
  isTourV2ExperienceItem,
  isTourV2Item,
  isPurchaseCreditItem,
} from 'lib/checkout/checkoutUtils'
import { EmptyArray, groupBy, sum } from 'lib/array/arrayUtils'
import {
  CHECKOUT_ITEM_TYPE_FLIGHT,
  GIFT_RECIPIENT_FORM_ID,
  PRIMARY_TRAVELLER_FORM_ID,
} from 'constants/checkout'
import { ITEM_TYPE_BEDBANK, ITEM_TYPE_EXPERIENCE, ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_BOOKING_PROTECTION, ITEM_TYPE_PURCHASE_CREDIT } from 'constants/cart'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { computeBaggageCostForPaxList } from 'checkout/lib/utils/flights/extras'
import {
  OFFER_DYNAMIC_PRICING_TYPES,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import { buildBaggagePayload, buildPassengerPayload } from 'checkout/lib/utils/flights/order'
import { getDateChangeJourneyV2IdKey, getEffectiveFlightPrice, getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import type { Order } from '@luxuryescapes/contract-svc-order'
import { generateScheduleDate } from 'lib/payment/gift'
import { getInstantBookingPriceAndSurcharge } from 'checkout/lib/utils/accommodation/price'
import getTransferItems from 'checkout/selectors/view/items/getTransferItems'
import getInsuranceItemsView from 'checkout/selectors/view/itemViews/getInsuranceItemsView'
import { isCheckoutPostPurchaseInsurance } from 'checkout/selectors/view/insurance'
import getCheckoutSelectedQuotes from 'checkout/selectors/view/getCheckoutSelectedQuote'
import { validateInsuranceTravellerCount, logInsuranceTravellerMismatch } from 'checkout/lib/utils/insurance/validateInsuranceTravellerCount'
import { getAge } from 'lib/datetime/dateUtils'
import { getCheckoutCruiseOfferView, isCruiseCheckout } from 'checkout/selectors/view/cruise'
import { getBookingSessionData } from 'checkout/selectors/cruiseSelectors'
import { TRANSPORTATION_CODE } from 'components/CarHire/CarHireHardcodedLists'
import {
  getAccommodationItems,
  getLeInstantBookingItems,
  getTourV2CheckoutItems,
} from 'checkout/selectors/view/accommodation'
import { getBundleAndSaveItemViews } from 'checkout/selectors/view/bundleAndSave'
import { getGiftCardItems } from 'checkout/selectors/view/giftCard'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import getCheckoutQuoteSessionId from 'checkout/selectors/request/getCheckoutQuoteSessionId'
import { baggageOptionsMap } from 'api/lib/convertJourneyV2'
import { getVillaViews } from '../view/villa'
import { getBookingProtectionItems } from '../view/bookingProtection'
import { getLuxPlusSubscriptionItems, checkoutWithMemberPrice, getSubscriptionJoinItems, getShouldUseCruiseMemberPrice } from '../view/luxPlusSubscription'
import { getFullOffers } from 'selectors/offerSelectors'
import getBookingProtectionDetails from 'checkout/selectors/request/getBookingProtectionDetails'
import { getCheckoutInsuranceTravellers } from 'checkout/selectors/request/getCheckoutInsuranceTravellers'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getBedbankChangeDatesTravellers } from '../bedbankChangeDatesTravellers'
import { getCoverGeniusAgeCategoryByDateOfBirth } from 'lib/insurance/insuranceHelpers'
import { getTourV2ExperienceItems } from '../view/toursv2'
import { getIsPostPurchaseTourChangeDates, getIsPostPurchaseTourOptionalExperience } from 'checkout/selectors/tourV2Selectors'
import { getCarHireSelectedRateOption } from 'checkout/lib/utils/carHire/view'
import { getOrderSubscriber } from 'luxPlus/selectors/checkout/order'
import { FlightViewTypes } from 'constants/flight'
import { floatify } from 'lib/maths/mathUtils'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'
import { getChannelMarkupDetails, getMemberPriceChannelMarkupDetails } from 'lib/channelMarkup/channelMarkup'
import type { PromoItemDiscountsWithStatus } from 'lib/promo/promoMappers'
import { filterItemDiscounts } from 'lib/promo/promoMappers'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'
import getIsLuxPlusPricedFlight from './getLuxPlusPricedFlight'
import { getBedbankItemIdToLeadItemId } from './accommodation'
import type { GetPromoCodeItemDiscounts } from './checkout'
import { getAccommodationAndExperienceItemDiscounts, getAllPromoCodeItemDiscounts, getCruiseItemDiscounts, getGiftCardItemDiscounts, getInsuranceItemDiscounts, getSubReoccuringItemDiscounts, getSubscriptionJoinDiscountItems, getTransferItemDiscounts } from './checkout'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getExperienceItemSourceApp from './getExperienceItemSourceApp'
import getFormattedLoyaltyLoungePassItems from './orderCreation/getFormattedLoyaltyLoungePassItems'
import { getFormattedExperienceItems } from './orderCreation/getFormattedExperienceItems'
import { getFormattedTravellers } from './orderCreation/getFormattedTravellers'
import formatItemDiscount from 'lib/payment/formatDiscountItem'
import type { ExperienceOrderCreationItem, LoyaltyPurchasePointsOrderCreationItem, MerchandiseOrderCreationItem, MerchandiseShippingOrderCreationItem } from 'api/order'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import getFormattedMerchandiseShippingItems from './orderCreation/getFormattedMerchandiseShippingItems'
import getFormattedMerchandiseItems from './orderCreation/getFormattedMerchandiseItems'
import getFormattedLoyaltyPurchasePointsItems from './orderCreation/getFormattedLoyaltyPurchasePointsItems'
import { getCartHasPropertyCollectItem } from './propertyCollectSelectors'
import getShouldEarnLoyaltyPoints from 'checkout/selectors/loyalty/getShouldEarnLoyaltyPoints'
import { getRoomGuestItems, getRoomGuestNames, isRoomGuestFieldsOffer, normaliseRoomGuestName } from 'checkout/lib/utils/roomGuestFields'
import type { RoomGuestItem } from 'checkout/lib/utils/roomGuestFields'

function parseStreetNumber(street: string): { streetNumber: string; streetName: string } {
  const match = street.match(/^(\d+[a-zA-Z]?)\s+(.+)$/)
  return match ? { streetNumber: match[1], streetName: match[2] } : { streetNumber: '', streetName: street }
}

export function getPassportCardFallbackTravellerForms(
  travellerForms: Array<App.Checkout.TravellerForm>,
  insuranceTravellers: App.Occupants,
): Array<App.Checkout.TravellerForm> {
  // PassportCard Accept must match the traveller count used for the quote.
  // If the item has no persisted PassportCard travellers, only fall back to the
  // checkout traveller forms that correspond to the quoted insurance occupancy.
  const adultCount = insuranceTravellers.adults ?? 0
  const childCount = (insuranceTravellers.children ?? 0) + (insuranceTravellers.infants ?? 0)
  const totalCount = adultCount + childCount

  const adultForms = travellerForms.filter(traveller => traveller.id.startsWith('adult-'))
  const childForms = travellerForms.filter(traveller => !traveller.id.startsWith('adult-'))
  const selectedForms = [
    ...adultForms.slice(0, adultCount),
    ...childForms.slice(0, childCount),
  ]

  if (selectedForms.length >= totalCount) return selectedForms

  const selectedFormIds = new Set(selectedForms.map(traveller => traveller.id))
  const remainingForms = travellerForms.filter(traveller => !selectedFormIds.has(traveller.id))

  return [
    ...selectedForms,
    ...remainingForms.slice(0, totalCount - selectedForms.length),
  ]
}

function getContactName(primaryContact: App.Checkout.TravellerForm | undefined, account: App.AuthAccount) {
  if (primaryContact) {
    return `${primaryContact.data.firstName} ${primaryContact.data.lastName}`
  } else {
    let contactName = account.givenName
    if (account.surname) contactName += ` ${account.surname}`
    return contactName
  }
}

function addConnectRoomsSpecialRequestMessage(specialRequest: string | undefined, contactNames: string | Array<string>) {
  const hasExistingSpecialRequest = !!specialRequest?.replace(/,/g, '').trim()
  const prefix = hasExistingSpecialRequest ? `${specialRequest} ` : ''
  return `${prefix}${getConnectRoomsSpecialRequestMessage(contactNames, getCurrentIntl())}`
}

interface InsuranceOrderTravellerDetail {
  firstname: string | undefined
  surname: string | undefined
  age_range: string
}

interface InsuranceOrderTravellerDetailsResult {
  travellersDetails: Array<InsuranceOrderTravellerDetail>
  isUsingCustomTravellers: boolean
}

export function shouldUseInsuranceItemCustomTravellers(
  item: App.Checkout.InsuranceItem,
  isCruiseCheckout: boolean,
  isPostPurchaseInsurance: boolean,
): boolean {
  const isCruisePostPurchase = isCruiseCheckout && isPostPurchaseInsurance
  return item.insuranceType !== 'cfmr' && !!item.travellers?.length && (!isCruiseCheckout || isCruisePostPurchase)
}

export function getCheckoutInsuranceOrderTravellerDetails(
  item: App.Checkout.InsuranceItem,
  travellers: Array<App.Checkout.TravellerForm>,
  isCruiseCheckout: boolean,
  isPostPurchaseInsurance: boolean,
): InsuranceOrderTravellerDetailsResult {
  const isUsingCustomTravellers = shouldUseInsuranceItemCustomTravellers(
    item,
    isCruiseCheckout,
    isPostPurchaseInsurance,
  )

  if (isUsingCustomTravellers) {
    return {
      isUsingCustomTravellers,
      travellersDetails: item.travellers!.map(traveller => ({
        firstname: traveller.firstName,
        surname: traveller.lastName,
        age_range: traveller.ageRange,
      })),
    }
  }

  return {
    isUsingCustomTravellers,
    travellersDetails: travellers.map(traveller => ({
      firstname: traveller.data.firstName,
      surname: traveller.data.lastName,
      age_range: traveller.data.dateOfBirth ? getCoverGeniusAgeCategoryByDateOfBirth(traveller.data.dateOfBirth) : traveller.id.split('-')[0],
    })),
  }
}

function getRoomGuestContactNames({
  roomGuestItems,
  primaryForm,
  fallbackFirstName,
  fallbackLastName,
}: {
  roomGuestItems: Array<RoomGuestItem>
  primaryForm: App.Checkout.TravellerForm | undefined
  fallbackFirstName: string | undefined
  fallbackLastName: string | undefined
}) {
  const contactNames = roomGuestItems.map((roomGuestItem, index) => {
    const guestNames = getRoomGuestNames({
      itemId: roomGuestItem.itemId,
      primaryForm,
      fallbackFirstName,
      fallbackLastName,
      isEnabled: true,
      isPrimaryRoom: index === 0,
    })

    return [guestNames.firstName, guestNames.lastName].filter(Boolean).join(' ').trim()
  }).filter(Boolean)

  return [...new Set(contactNames)]
}

function isPrimaryTravellerName(
  firstName: unknown,
  lastName: unknown,
  primaryForm: App.Checkout.TravellerForm | undefined,
) {
  return normaliseRoomGuestName(firstName) === normaliseRoomGuestName(primaryForm?.data.firstName) &&
    normaliseRoomGuestName(lastName) === normaliseRoomGuestName(primaryForm?.data.lastName)
}

export { getFormattedTravellers }

export const getFormattedTransferItems = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getTransferItemDiscounts(state),
  (state: App.State) => getExperienceItemSourceApp(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getIsEnabledLuxPlusDiscountedExperiences(state),
  (state: App.State) => getFormattedTravellers(state),
  (state: App.State) => getBedbankItemIdToLeadItemId(state),
  (transferItems, experiences, transferItemDiscounts, sourceApp, checkoutWithMemberPrice, isLuxPlusDiscountedExperiencesEnabled, formattedTravellers, bedbankItemIdToLeadItemId) => {
    return transferItems.map((item) => {
      const experience = experiences[item.experienceId]
      const { transfer, transactionKey } = item

      if (experience && transfer.option && transferItemDiscounts.hasRequiredPromoCodeData) {
        const [hour, minute] = transfer.time?.split(':') ?? ['00', '00']
        const fullDateTime = moment.utc(transfer.date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')

        // if member price is available, use it, otherwise use the price
        let price = transfer.option.price
        let memberPriceDiscountAmount = 0
        if (isLuxPlusDiscountedExperiencesEnabled && checkoutWithMemberPrice && transfer.option.memberPrice > 0) {
          price = transfer.option.memberPrice
          memberPriceDiscountAmount = Math.max(transfer.option.price - price, 0)
        }

        const itemDiscounts = filterItemDiscounts({ itemDiscounts: transferItemDiscounts.itemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })

        // Multiple bedbank cart items (that belong to the same session) become grouped into a
        // single formatted bedbank item; we must normalise our parent cart item ID references
        // accordingly.
        const parentCartItemIds = item.bundledItemIds
          ? [...new Set(item.bundledItemIds.map(itemId => bedbankItemIdToLeadItemId.get(itemId) ?? itemId))]
          : undefined

        return {
          type: ITEM_TYPE_EXPERIENCE,
          provider_offer_id: experience.id,
          brand: config.BRAND,
          transaction_key: transactionKey,
          total: price,
          taxes_and_fees: transfer.option.taxesAndFees,
          is_accom_bundle: item.isAccomBundle,
          parent_cart_item_ids: parentCartItemIds,
          tickted: experience.ticketed,
          title: experience.name,
          categories: experience.categories,
          cancellation_policies: experience.cancellationPolicies.length > 0 ? {
            timezoneOffset: `${new Date().getTimezoneOffset()}`,
            refundPolicies: experience.cancellationPolicies.map(policy => ({
              id: policy.id,
              periodLabel: policy.periodLabel,
              periods: policy.periods,
              value: policy.value,
              groupId: policy.groupId,
            })),
          } : undefined,
          le_exclusive: experience.leExclusive,
          special_requests: transfer.specialRequests,
          ...formattedTravellers && {
            traveller_info: {
              customer: formattedTravellers?.customer,
            },
          },
          pickup_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.name : experience.airport?.name,
          pickup_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.googlePlaceId : experience.airport?.code,
          dropoff_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? (transfer.isHomePickup ? transfer.airport?.name : experience.airport?.name) : transfer.hotel.name,
          dropoff_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? (transfer.isHomePickup ? transfer.airport?.code : experience.airport?.code) : transfer.hotel.googlePlaceId,
          // LED provider experiences use redemption_location_id to identify the booking location
          ...(transfer.redemptionLocationId && { redemption_location_id: transfer.redemptionLocationId }),
          // Home-to-airport transfers omit deal_options so the order stores them as
          // experience items (not transfers), matching the existing agent-purchased flow.
          deal_options: !transfer.isHomePickup ? {
            adults: transfer.travellers.adults,
            children: transfer.travellers.children ?? 0,
            child_seats: transfer.travellers.childSeats ?? 0,
            booster_seats: transfer.travellers.boosterSeats ?? 0,
            option: transfer.option,
            flight_number: transfer.flightNumber,
            type: transfer.type,
            bags: transfer.baggage?.bags ?? 0,
            oversized_bags: transfer.baggage?.oversizedBags ?? 0,
          } : undefined,
          ticket: {
            date: fullDateTime,
            // time is optional so we need separate fields to know whether it was set or not
            day: transfer.date,
            time: transfer.time,
            fareType: transfer.option.name,
            identifier: transfer.option.id,
            productId: transfer.option.id,
            type: transfer.isHomePickup ? undefined : transfer.type,
          },
          book_by_date: transfer.option?.bookByDate,
          ...((transfer.option?.discounts?.app.amount ?? 0) > 0 && {
            app_discount_amount: transfer.option.discounts?.app.amount,
            app_discount_percent: transfer.option.discounts?.app.percentage,
          }),
          member_discount_amount: memberPriceDiscountAmount,
          item_discounts: itemDiscounts.map(d => formatItemDiscount(d, transactionKey)),
          source_app: sourceApp,
        }
      }

      return null
    })
  },
)

const getFormattedBundleAndSaveItems = createSelector(
  (state: App.State) => getBundleAndSaveItemViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.auth.account,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (views, travellerForms, account, specialRequests, promoCodeItemDiscounts) => {
    const data = views.data

    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    return data.flatMap((view) => {
      return view.hotels.map(hotelView => {
        const hotelViewPrice = hotelView.totals.price
        const newPrice = view.item.dates[hotelView.offer.id]?.newPrice
        const newSurcharge = view.item.dates[hotelView.offer.id]?.newSurcharge
        const newExtraGuestSurcharge = view.item.dates[hotelView.offer.id]?.newExtraGuestSurcharge

        const itemDiscounts = filterItemDiscounts({ itemDiscounts: promoCodeItemDiscounts.itemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [view.item.itemId] } })
        const bundleSaveTransactionKey = uuidV4()
        // TODO: We don't have an official type for order item creation, create it
        const orderItem: any = {
          bundle_offer_id: view.bundleOffer.id,
          bundle_package_id: view.bundlePackage.id,
          reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
          check_in: moment.utc(hotelView.checkIn).format(ISO_DATE_FORMAT),
          check_out: moment.utc(hotelView.checkOut).format(ISO_DATE_FORMAT),
          number_of_nights: hotelView.duration,
          number_of_adults: view.occupancy.adults,
          number_of_children: view.occupancy.children,
          number_of_infants: view.occupancy.infants,
          number_of_teenagers: view.occupancy.teenagers,
          children_ages: view.occupancy.childrenAge ?? [],
          rate_plan_id: hotelView.package.roomRate?.id,
          guest_first_name: primaryTraveller?.data.firstName,
          guest_last_name: primaryTraveller?.data.lastName,
          cart_item_id: view.item.itemId,
          offer_id: hotelView.offer.id,
          package_id: hotelView.package.id,
          room_rate_id: hotelView.package.roomRate?.id,
          transaction_key: bundleSaveTransactionKey,
          offer_booking_type: 'reservation',
          offer_type: hotelView.offer.type,
          offer_name: hotelView.offer.name,
          package_name: hotelView.package.name,
          offer_slug: view.bundleOffer.slug,
          price: newPrice || hotelViewPrice,
          value: hotelView.package.value,
          surcharge: newSurcharge ?? hotelView.totals.surcharge - hotelView.totals.extraGuestSurcharge,
          guest_special_requests: specialRequests[view.item.itemId],
          extra_guest_surcharge: newExtraGuestSurcharge ?? hotelView.totals.extraGuestSurcharge,
          property_fees: hotelView.totals.otherFees?.propertyFees,
          localizedPrices: hotelView.package.trackingPrice,
          customer_email: primaryTraveller?.data.email,
          customer_name: account?.fullName,
          room_rate_amount: undefined,
          item_discounts: itemDiscounts.map(d => formatItemDiscount(d, bundleSaveTransactionKey)),
        }

        if (OFFER_DYNAMIC_PRICING_TYPES.includes(hotelView.offer.type)) {
          orderItem.room_rate_amount = newPrice || hotelViewPrice
        }

        return orderItem
      })
    })
  },
)

const getFormattedTourV2Items = createSelector(
  (state: App.State) => getTourV2CheckoutItems(state),
  (state: App.State) => getTourV2ExperienceItems(state),
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (
    tourV2Items,
    tourExperiencesItems,
    specialRequests,
    offers,
    travellerForms,
    schemaData,
    promoCodeItemDiscounts,
    checkoutWithMemberPrice,
    isTourDateChange,
    isPostPurchaseTourOptionalExperience,
  ): App.WithDataStatus<Array<any>> => {
    const groupedTourItems = groupBy(tourV2Items, (item: App.Checkout.TourV2Item) => item.purchasableOption.fkDepartureId)
    if (groupedTourItems.size === 0) {
      return {
        data: [],
        hasRequiredData: tourV2Items.filter(isTourV2Item).length === 0,
      }
    }
    if (!promoCodeItemDiscounts.hasRequiredPromoCodeData) {
      return { data: [], hasRequiredData: false }
    }

    // Don't send tour items for post-purchase optional experience purchases
    // Only the tour_optional_experience item should be sent (handled by getFormattedTourExperienceItems)
    if (isPostPurchaseTourOptionalExperience) {
      return { data: [], hasRequiredData: true }
    }

    if (isTourDateChange) {
      const formattedTourV2Items = Array.from(groupedTourItems.values())
        .map(itemGroup => {
          const offer = offers[itemGroup[0].offerId] as App.Tours.TourV2Offer
          return formatTourV2ItemGroupDateChanges(itemGroup, offer, checkoutWithMemberPrice)
        })
      return { data: formattedTourV2Items, hasRequiredData: true }
    }

    if (schemaData && travellerForms.length > 0) {
      const formattedTourV2Items = Array.from(groupedTourItems.values())
        .map(itemGroup => {
          const offer = offers[itemGroup[0].offerId] as App.Tours.TourV2Offer
          const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
          if (!offer || !primaryTraveller) {
            return { data: EmptyArray, hasRequiredData: false }
          }
          return formatTourV2ItemGroup(
            itemGroup,
            tourExperiencesItems,
            specialRequests,
            travellerForms,
            primaryTraveller,
            offer,
            schemaData.accommodationFlightSchema,
            checkoutWithMemberPrice,
            promoCodeItemDiscounts.itemDiscounts,
          )
        })
      return { data: formattedTourV2Items, hasRequiredData: true }
    }
    return {
      data: [],
      hasRequiredData: tourV2Items.filter(isTourV2Item).length === 0,
    }
  },
)

const getFormattedTourExperienceItems = createSelector(
  (state: App.State) => getTourV2ExperienceItems(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (tourExperienceItems: Array<App.Checkout.TourV2ExperienceItem>, isPostPurchaseTourExperience: boolean): App.WithDataStatus<Array<any>> => {
    return {
      data: isPostPurchaseTourExperience ? [{
        totalprice: sum(tourExperienceItems.map(item => item.total)),
        optional_experiences: tourExperienceItems.map(formatTourExperienceItem),
        type: 'tour_optional_experience',
        transaction_key: uuidV4(),
      }] : [],
      hasRequiredData: isPostPurchaseTourExperience ? tourExperienceItems.filter(isTourV2ExperienceItem).length === 0 : true,
    }
  },
)

export const getFormattedCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getCheckoutCruiseOfferView(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCruiseItemDiscounts(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getShouldEarnLoyaltyPoints(state),
  (state: App.State) => getBookingSessionData(state),
  (state: App.State) => getShouldUseCruiseMemberPrice(state),
  (items, offerViewsWithStatus, travellerForms, { itemDiscounts: cruiseItemDiscounts, hasRequiredPromoCodeData }, formSchemaData, shouldEarnLoyaltyPoints, sessionData, shouldUseCruiseMemberPrice): App.WithDataStatus<Array<any>> => {
    const cruiseItems = items.filter(isCruiseItem)
    if (cruiseItems.length === 0) {
      return { data: [], hasRequiredData: true }
    }

    const formSchema = formSchemaData?.accommodationFlightSchema
    if (!travellerForms || !offerViewsWithStatus.hasRequiredData || !formSchema) {
      return { data: [], hasRequiredData: false }
    }

    const personTitles = sessionData.personTitles ?? []
    const offerViews = offerViewsWithStatus.data
    const result = offerViews.flatMap(offerView => {
      const { itemViews } = offerView
      return itemViews.map(itemView => {
        const { departure } = itemView
        const itemTravellers = travellerForms.filter(form => formSchema.properties[form.id]?.relatedItem?.id === itemView.item.itemId)
        const itemDiscounts = filterItemDiscounts({
          itemDiscounts: cruiseItemDiscounts,
          lookup: { specificType: 'cart-id-based', cartItemIds: [itemView.item.itemId] },
        })

        if (!departure || !itemTravellers.length || !hasRequiredPromoCodeData) { return null }

        return formatCruiseItem(itemView, itemTravellers, itemDiscounts, shouldEarnLoyaltyPoints, personTitles, shouldUseCruiseMemberPrice)
      })
    }).filter(excludeNullOrUndefined)

    return { data: result, hasRequiredData: result.length === cruiseItems.length }
  },
)

const getFormattedInsuranceItems = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (state: App.State) => getOrderSubscriber(state),
  (state: App.State) => getInsuranceItemDiscounts(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => getCheckoutSelectedQuotes(state),
  (state: App.State) => state.checkout.cart.cartId,
  (state: App.State) => state.auth.account,
  (state: App.State) => getCheckoutQuoteSessionId(state),
  (
    itemView,
    travellers,
    insuranceTravellers,
    subscriber,
    insuranceItemDiscounts,
    isCruiseCheckout,
    isPostPurchaseInsurance,
    selectedQuotes,
    cartId,
    account,
    quoteSessionId,
  ) => {
    const primaryTraveller = travellers.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    const viewItemsWithQuotes = itemView.data.filter(viewItem => viewItem.quoteId)

    const hasItemsRequiringTravellerForms = viewItemsWithQuotes.some(viewItem => !shouldUseInsuranceItemCustomTravellers(
      viewItem.item,
      isCruiseCheckout,
      isPostPurchaseInsurance,
    ))

    // Custom traveller items can be submitted without checkout traveller forms.
    if (!primaryTraveller && hasItemsRequiringTravellerForms) {
      return []
    }

    if (!insuranceItemDiscounts.hasRequiredPromoCodeData) {
      return []
    }

    // views must have a quote associated with them -
    // those without a quote id are most likely post purchase updates and are handled via
    // the specific post purchase operation
    return viewItemsWithQuotes.map(viewItem => {
      // getInsuranceItemsView already resolves totals.memberPrice to 0 for any item this buyer
      // isn't genuinely eligible for (whether via the legacy flag or flight-CFMR's own real
      // membership check) - so a per-item value check is the correct source of truth for what
      // gets charged, not the single flag-based useMemberPrice, which would otherwise still
      // submit the full price for a flight CFMR item whenever the legacy flag happens to be off.
      const total = viewItem.totals.memberPrice > 0 ? viewItem.totals.memberPrice : viewItem.totals.price
      const isPassportCardItem = viewItem.item.productId === PRODUCT_ID_PASSPORT_CARD
      const {
        travellersDetails,
        isUsingCustomTravellers,
      } = getCheckoutInsuranceOrderTravellerDetails(
        viewItem.item,
        travellers,
        isCruiseCheckout,
        isPostPurchaseInsurance,
      )

      if (isPassportCardItem) {
        const fallbackAddress: App.PassportCardTravellerAddress = {
          country: 'AU',
          state: primaryTraveller?.data.state || 'Unknown',
          street: primaryTraveller?.data.address || 'Unknown',
          city: primaryTraveller?.data.city || 'Unknown',
          postcode: primaryTraveller?.data.postcode || '0000',
        }
        // Avoid inflating the PassportCard Accept payload with unrelated checkout
        // traveller forms; PassportCard rejects Accept when it diverges from Quote.
        const fallbackTravellerForms = getPassportCardFallbackTravellerForms(travellers, insuranceTravellers)
        const fallbackPassportCardTravellers: Array<App.PassportCardInsuranceTraveller> = fallbackTravellerForms.map((traveller, index) => ({
          firstName: traveller.data.firstName || '',
          lastName: traveller.data.lastName || '',
          ageRange: traveller.id.startsWith('adult-') ? 'adult' : 'child',
          email: traveller.data.email || '',
          phonePrefix: traveller.data.phonePrefix || '',
          phone: traveller.data.phone || '',
          dateOfBirth: traveller.data.dateOfBirth || '',
          ...(index === 0 ? {
            address: {
              ...fallbackAddress,
              street: traveller.data.address || fallbackAddress.street,
            },
          } : {}),
        }))

        const passportCardTravellers = viewItem.item.passportCardTravellers?.length
          ? viewItem.item.passportCardTravellers
          : fallbackPassportCardTravellers

        const policyHolderPhone = (passportCardTravellers[0]?.phonePrefix && passportCardTravellers[0]?.phone)
          ? `+${passportCardTravellers[0].phonePrefix}${passportCardTravellers[0].phone}`
          : ''
        const insuredPersons = passportCardTravellers.slice(1)
        const policyAddress = passportCardTravellers[0]?.address
        const parsedPolicyStreet = parseStreetNumber(policyAddress?.street || fallbackAddress.street || '')

        return {
          type: ITEM_TYPE_INSURANCE,
          provider: 'passport_card',
          product_id: viewItem.item.productId,
          quote_id: viewItem.quoteId,
          // Correlation id threaded to svc-order -> svc-insurance. Must equal the
          // session_id used at PassportCard quote time (getCheckoutQuoteSessionId).
          session_id: quoteSessionId,
          proposal_id: viewItem.item.passportCardProposalId ?? '',
          optional_benefits: viewItem.item.passportCardOptionalBenefitCodes?.length && viewItem.item.passportCardProposalId
            ? { [viewItem.item.passportCardProposalId]: viewItem.item.passportCardOptionalBenefitCodes }
            : undefined,
          brand: config.BRAND,
          transaction_key: viewItem.item.transactionKey,
          policy_holder_firstname: passportCardTravellers[0]?.firstName || '',
          policy_holder_surname: passportCardTravellers[0]?.lastName || '',
          travellers_details: passportCardTravellers.map(traveller => ({
            firstname: traveller.firstName,
            surname: traveller.lastName,
            age_range: traveller.ageRange,
          })),
          consumer_details: {
            salute: primaryTraveller?.data.title || 'Mr',
            gender: '', // RODO: required for now, remove later
            firstName: passportCardTravellers[0]?.firstName || '',
            middleName: '',
            lastName: passportCardTravellers[0]?.lastName || '',
            email: passportCardTravellers[0]?.email || '',
            phone: policyHolderPhone,
            dateOfBirth: passportCardTravellers[0]?.dateOfBirth || '',
            personalId: account?.memberId || primaryTraveller?.id || viewItem.item.itemId,
          },
          insured_persons: insuredPersons.map(traveller => ({
            firstName: traveller.firstName,
            middleName: '',
            lastName: traveller.lastName,
            gender: '', // TODO: required for now, remove later
            dateOfBirth: traveller.dateOfBirth,
          })),
          address: {
            country: policyAddress?.country || fallbackAddress.country || 'AU',
            state: policyAddress?.state || fallbackAddress.state || '',
            street: parsedPolicyStreet.streetName,
            street2: policyAddress?.unitNumber || '',
            streetNumber: parsedPolicyStreet.streetNumber,
            city: policyAddress?.city || fallbackAddress.city || '',
            zip: policyAddress?.postcode || fallbackAddress.postcode || '',
          },
          insurance_type: viewItem.item.insuranceType,
          total: floatify(total),
          subscriber,
          mobile_only_price: !!viewItem.mobileAppPrice,
          item_discounts: insuranceItemDiscounts.itemDiscounts.map(d => formatItemDiscount(d, viewItem.item.transactionKey)),
          flight_journeys_keys: viewItem.item.flightJourneysKeys,
          bedbank_room_ids: viewItem.item.bedbankRoomIds,
        }
      }

      const quote = selectedQuotes[viewItem.item.itemId]

      // Log residual quote/order traveller mismatches. The quote and traveller schema
      // should already be aligned before this payload is built.
      const validationResult = validateInsuranceTravellerCount(
        quote,
        travellersDetails as Array<{ firstname: string | undefined; surname: string | undefined; age_range: string }>,
        viewItem.item.itemId,
        viewItem.item.insuranceType ?? 'unknown',
        !!isUsingCustomTravellers,
        isCruiseCheckout,
      )

      if (!validationResult.isValid && validationResult.mismatch) {
        logInsuranceTravellerMismatch(validationResult.mismatch, {
          cartId,
          travellerFormsCount: travellers.length,
          customTravellersCount: viewItem.item.travellers?.length ?? 0,
          hasItemsRequiringTravellerForms,
          isUsingCustomTravellers,
          quoteStartDate: quote?.startDate,
          quoteEndDate: quote?.endDate,
          quoteTotal: quote?.total,
        })
      }

      return {
        type: ITEM_TYPE_INSURANCE,
        product_id: viewItem.item.productId,
        quote_id: viewItem.quoteId,
        brand: config.BRAND,
        transaction_key: viewItem.item.transactionKey,
        policy_holder_firstname: isUsingCustomTravellers && !primaryTraveller ? travellersDetails[0]?.firstname : primaryTraveller?.data.firstName ?? '',
        policy_holder_surname: isUsingCustomTravellers && !primaryTraveller ? travellersDetails[0]?.surname : primaryTraveller?.data.lastName ?? '',
        travellers_details: travellersDetails,
        insurance_type: viewItem.item.insuranceType,
        total: floatify(total),
        subscriber,
        mobile_only_price: !!viewItem.mobileAppPrice,
        item_discounts: insuranceItemDiscounts.itemDiscounts.map(d => formatItemDiscount(d, viewItem.item.transactionKey)),
        flight_journeys_keys: viewItem.item.flightJourneysKeys,
        bedbank_room_ids: viewItem.item.bedbankRoomIds,
      }
    })
  },
)

const getFormattedBookingProtectionItems = createSelector(
  (state: App.State) => getBookingProtectionItems(state),
  (state: App.State) => getBookingProtectionDetails(state),
  (bookingProtectionItems, bookingProtectionDetails): Array<App.Checkout.BookingProtectionDetails> | [] => {
    if (!bookingProtectionDetails) return []

    return bookingProtectionItems.map(item => ({
      type: ITEM_TYPE_BOOKING_PROTECTION,
      transaction_key: item.transactionKey,
      ...bookingProtectionDetails,
    }))
  },
)

export const getFormattedGiftCardItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => getGiftCardItemDiscounts(state),
  (account, items, giftCardItemDiscounts) => {
    const allItemDiscounts = giftCardItemDiscounts.hasRequiredPromoCodeData ? giftCardItemDiscounts.itemDiscounts : EmptyArray
    return items.map((item) => {
      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })

      return {
        type: ITEM_TYPE_GIFT_CARD,
        brand: config.BRAND,
        transaction_key: item.transactionKey,
        gift_card_value: item.amount,
        customer_email: account.email,
        customer_name: account.fullName,
        customer_phone: item.content.fromPhone || account.phone,
        customer_phone_prefix: item.content.fromPhonePrefix || account.phonePrefix,
        personalised: {
          method: item.deliveryMethod,
          to: item.content.recipientName,
          from: item.content.fromName,
          message: item.content.message,
          email_of_recipient: item.content.recipientEmail,
          is_blank_message: !item.content.message,
        },
        item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
        product: item.product ? {
          name: item.product.name,
          items: item.product.items,
          imageId: item.product.image.id,
          imageUrl: item.product.image.url,
          type: item.product.type,
          url: item.product.url,
          location: item.product.location,
        } : undefined,
      }
    })
  },
)

export function formatCartItems(
  cartState: App.CheckoutCartState,
  connectedRoomOfferIds: Array<string> = [],
  offers: Record<string, App.AnyOffer>,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Record<string, App.AnyJourney>,
  account: App.AuthAccount,
  isTravellerFormNeeded: boolean,
  formSchemaRequest: { items: Array<any> },
  formattedInstantBookingHotelItems: App.WithDataStatus<Array<unknown>>,
  formattedTourV2Items: App.WithDataStatus<Array<unknown>>,
  bedbankItemIdToLeadItemId: Map<string, string>,
  formattedExperienceItems: App.WithDataStatus<Array<ExperienceOrderCreationItem>>,
  formattedTransferItems: Array<unknown>,
  formattedInsuranceItems: Array<unknown>,
  getFormattedBookingProtectionItems: Array<unknown>,
  formattedCruiseItems: App.WithDataStatus<Array<unknown>>,
  formattedBundleAndSaveItems: Array<unknown>,
  formattedGiftCardItems: Array<unknown>,
  formattedVillaItems: Array<unknown>,
  formattedLuxPlusSubscriptionItems: Array<FormattedOrderSubscriptionItem>,
  formattedSubscriptionJoinFeeItems: Array<FormattedOrderSubscriptionItem>,
  checkoutWithMemberPrice: boolean,
  formattedTourExperienceItems: App.WithDataStatus<Array<unknown>>,
  existingOrderTraveller: App.ExistingOrderBedbankTraveller | undefined,
  promoCodeItemDiscounts: PromoItemDiscountsWithStatus,
  formattedLoyaltyLoungepassItems: Array<ExperienceOrderCreationItem>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  formattedMerchandiseItems: Array<MerchandiseOrderCreationItem>,
  formattedLoyaltyPurchasePointsItems: Array<LoyaltyPurchasePointsOrderCreationItem>,
  formattedMerchandiseShippingItems: App.WithDataStatus<Array<MerchandiseShippingOrderCreationItem>>,
  cartHasPropertyCollectItem: boolean,
  regionCode: string,
  channelMarkup?: App.ChannelMarkup,
  sessions?: Record<string, {
    data: App.BedbankEnquiry | null
    state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
  }>,
  isLuxPlusPriced: boolean = false,
): App.WithDataStatus<Array<any>> {
  const { itemDiscounts: allItemDiscounts, hasRequiredPromoCodeData } = promoCodeItemDiscounts

  if (
    isTravellerFormNeeded &&
    formSchemaRequest.items.length > 0 &&
    !travellerForms.some(form => form.id === PRIMARY_TRAVELLER_FORM_ID) ||
    !promoCodeItemDiscounts.hasRequiredPromoCodeData
  ) {
    return { hasRequiredData: false, data: [] }
  }

  let hasRequiredData = true

  const { items, specialRequests, arrivalFlightNumber } = cartState
  const roomGuestItems = config.agentHub.isEnabled ? getRoomGuestItems(items.filter(isAccommodationItem), offers) : EmptyArray

  const formattedBNBLHotelItems = items
    .filter(isBNBLLEHotelItem)
    .map(item => {
      const offer = offers[item.offerId] as App.Offer
      if (!offer) {
        hasRequiredData = false
        return null
      }

      return formatLeHotelItem(
        item,
        travellerForms,
        specialRequests[item.itemId],
        offer,
        undefined,
        account,
        checkoutWithMemberPrice,
        connectedRoomOfferIds,
        undefined,
        undefined,
        allItemDiscounts,
        channelMarkup,
      )
    })

  const formattedTourV1Items = items
    .filter(isTourV1Item)
    .map((item, idx) => {
      const offer = offers[item.offerId] as App.Offer
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts

      if (!offer) {
        hasRequiredData = false
        return null
      }

      if (travellerForms[idx]) {
        const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })
        return formatLeTourV1Item(item, travellerForms[idx], specialRequests[item.itemId], offer, account, itemDiscounts)
      }
    })

  const formattedCarHireItems = items
    .filter(isCarHireItem)
    .map((item) => formatCarHireItem(item, travellerForms, arrivalFlightNumber, allItemDiscounts))

  // Bedbank cart room items need to be grouped together
  // Until the implementation changes on `svc-order`
  // As of now, there will only ever be 1 group
  const bedbankItems = items.filter(isBedbankItem)
  const groupedBedbankItems = [...groupBy(bedbankItems, item => item.sessionId).values()]

  const formattedBedbankItems = groupedBedbankItems.map(group => {
    const firstItem = group[0]
    // For bundled experience/transfer items, we must ensure they can consistently resolve to this
    // "lead" item ID.
    const leadItemId = bedbankItemIdToLeadItemId.get(firstItem.itemId) ?? firstItem.itemId
    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
    const offer = offers[firstItem.offerId] as App.BedbankOffer
    if (!offer) {
      hasRequiredData = false
      return null
    }
    const requests = group.map(item => item.itemId).map(id => specialRequests?.[id] ?? '').join(',')
    const itemDiscounts = filterItemDiscounts({
      itemDiscounts: allItemDiscounts,
      lookup: { specificType: 'cart-id-based', cartItemIds: group.map(item => item.itemId) },
    })

    return formatBedbankItem(leadItemId, firstItem, group, requests, primaryTraveller, roomGuestItems, itemDiscounts, cartHasPropertyCollectItem, regionCode, offer, channelMarkup, existingOrderTraveller, sessions)
  }).filter(Boolean)

  const formattedFlightItems = items
    .filter(isFlightItem)
    .reduce<Array<unknown>>((formattedItems, item) => {
      let flightJourneys: Array<App.JourneyV2> = []
      const isFlightDateChange = !!item.isFlightDateChange

      if (item.searchId) {
        let keys: Array<string> = []
        if (isFlightDateChange) {
          keys = item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight?.journeyId))
        } else {
          keys = item.flights.map(flight => getJourneyV2IdKey(flight.journeyId, item.searchId!, flight.fareFamily?.id))
        }
        flightJourneys = keys.map(key => journeys[key] as App.JourneyV2)
      }

      const hasAllJourneys = item.flights.length > 0 &&
        flightJourneys.length === item.flights.length &&
        flightJourneys.every(journey => !!journey)

      if (!hasAllJourneys || !item.hasFinalFare) {
        hasRequiredData = false
      }

      const data = hasAllJourneys ? [
        ...formattedItems,
        ...formatFlightItem(item, travellerForms, flightJourneys, allItemDiscounts, schemaData, isFlightDateChange, isLuxPlusPriced),
      ] : formattedItems

      return data
    }, [])

  // Check if data requirements satisfied
  hasRequiredData = hasRequiredData &&
    formattedInstantBookingHotelItems.hasRequiredData &&
    formattedTourV2Items.hasRequiredData &&
    formattedExperienceItems.hasRequiredData &&
    formattedCruiseItems.hasRequiredData &&
    formattedTourExperienceItems.hasRequiredData &&
    formattedMerchandiseShippingItems.hasRequiredData &&
    hasRequiredPromoCodeData

  const formattedPurchaseCreditItems = items
    .filter(isPurchaseCreditItem)
    .map((item) => ({
      type: ITEM_TYPE_PURCHASE_CREDIT,
      brand: config.BRAND,
      transaction_key: item.transactionKey,
      purchase_credit_value: item.amount,
      customer_email: account.email,
      customer_name: account.fullName,
    }))

  const formattedItems = [
    ...formattedInstantBookingHotelItems.data,
    ...formattedBNBLHotelItems,
    ...formattedTourV1Items,
    ...formattedTourV2Items.data,
    ...formattedBedbankItems,
    ...formattedFlightItems,
    ...formattedExperienceItems.data,
    ...formattedTransferItems,
    ...formattedInsuranceItems,
    ...getFormattedBookingProtectionItems,
    ...formattedCruiseItems.data,
    ...formattedCarHireItems,
    ...formattedBundleAndSaveItems,
    ...formattedGiftCardItems,
    ...formattedVillaItems,
    ...formattedLuxPlusSubscriptionItems,
    ...formattedSubscriptionJoinFeeItems,
    ...formattedTourExperienceItems.data,
    ...formattedLoyaltyLoungepassItems,
    ...formattedMerchandiseItems,
    ...formattedMerchandiseShippingItems.data,
    ...formattedPurchaseCreditItems,
    ...formattedLoyaltyPurchasePointsItems,
  ]

  return {
    hasRequiredData,
    data: formattedItems,
  }
}

function formatLeHotelItem(
  item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BNBLLEHotelItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  specialRequest: string,
  offer: App.Offer,
  instantBookingPricing: (App.Checkout.ItemViewTotals & {
    extraGuestSurchargesPayableAtProperty?: boolean
  }) | undefined,
  account: App.AuthAccount,
  checkoutWithMemberPrice: boolean,
  connectedRoomOfferIds: Array<string>,
  cartMode?: App.CheckoutCartMode,
  arrivalDetails?: App.ArrivalDetails,
  allItemDiscounts?: Array<App.Checkout.ItemDiscount>,
  channelMarkup?: App.ChannelMarkup,
  roomGuestIndex?: number,
  connectedRoomContactNames?: Array<string>,
) {
  let pkgUniqueKey = getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)
  if (item.loyaltyAppliedUpgradeType) {
    const originPackage = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)
    const upgradeTargetPackageUniqueKey = originPackage?.loyalty.upgradeTargetsUniqueKeys?.[item.loyaltyAppliedUpgradeType]
    if (upgradeTargetPackageUniqueKey) {
      // @ts-expect-error string satisfies the magic string here
      pkgUniqueKey = upgradeTargetPackageUniqueKey
    }
  }
  const pkg = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)

  if (!pkg) {
    throw new Error(`Package with unique key ${pkgUniqueKey} not found in offer ${offer.id}`)
  }

  // primaryTraveller will be absent on package upgrading
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const priceFields = {
    price: instantBookingPricing?.price ?? pkg.price ?? 0,
    memberPrice: offer.type === OFFER_TYPE_HOTEL
      ? (instantBookingPricing?.memberPrice || pkg.memberPrice) ?? 0
      : (instantBookingPricing?.memberPrice) ?? 0,
    taxesAndFees: instantBookingPricing?.taxesAndFees ?? pkg.taxesAndFees ?? 0,
    surcharge: instantBookingPricing?.surcharge ?? 0,
    extraGuestSurcharge: instantBookingPricing?.extraGuestSurcharge ?? 0,
    extraGuestSurchargesPayableAtProperty: instantBookingPricing?.extraGuestSurchargesPayableAtProperty ?? false,
  }
  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts ?? [], lookup: { specificType: 'accommodationAndExperiences', itemIds: [item.itemId] } })

  const offerSupportsLuxPlus = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA].includes(offer.type)

  // Channel markup for LPC: the displayed `price`/`lux_plus_price` carry the markup,
  // but svc-reservation validates the *un-marked* base rate. Send the marked price for
  // charging while keeping `room_rate_amount`/member base un-marked. When markup is not
  // applied (flag off / non-channel traffic) base === price, so behaviour is unchanged.
  const channelMarkupApplied = getChannelMarkupDetails(offer.id, offer.type, channelMarkup).isApplied
  const memberChannelMarkupApplied = getMemberPriceChannelMarkupDetails(offer.id, offer.type, channelMarkup).isApplied
  const memberLuxPlusPrice = memberChannelMarkupApplied
    ? (instantBookingPricing?.baseMemberRoomRate ?? priceFields.memberPrice)
    : priceFields.memberPrice

  const newBookingFields = {
    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,
    offer_booking_type: 'reservation',
    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    number_of_nights: pkg.duration,
    value: pkg.value,
    localizedPrices: pkg.trackingPrice,
    price: checkoutWithMemberPrice && priceFields.memberPrice > 0 ? priceFields.memberPrice : priceFields.price,
    public_price: offerSupportsLuxPlus ? priceFields.price : undefined,
    lux_plus_price: offerSupportsLuxPlus && priceFields.memberPrice > 0 ? memberLuxPlusPrice : undefined,
    surcharge: priceFields.surcharge,
    extra_guest_surcharge: priceFields.extraGuestSurchargesPayableAtProperty ? 0 : priceFields.extraGuestSurcharge,
    extra_guest_surcharge_payable_at_property: priceFields.extraGuestSurchargesPayableAtProperty ? priceFields.extraGuestSurcharge : 0,
    reservation_type: item.reservationType,
  }

  if (item.newPrice) {
    // accept a new price due to price change
    newBookingFields.price = item.newPrice
  }
  if (item.newSurcharge) {
    newBookingFields.surcharge = item.newSurcharge
  }
  if (item.newExtraGuestSurcharge) {
    newBookingFields.extra_guest_surcharge = item.newExtraGuestSurcharge
  }

  const selectDateFields = {
    ...(cartMode == 'select-date' && 'orderItemId' in item && {
      type: 'reservation',
      itemId: item.orderItemId,
      peak_period_surcharge: priceFields.surcharge,
      extra_guest_surcharge: priceFields.extraGuestSurcharge,
    }),
  }

  const arrivalDetailFields = {
    ...(arrivalDetails && {
      arrival_date: moment.utc(arrivalDetails.arrivalDate).format(ISO_DATE_FORMAT),
      arrival_time: arrivalDetails.arrivalTime,
      arrival_flight_number: arrivalDetails.arrivalFlightNumber,
    }),
  }

  let specialRequestWithConnectedRoomsMessage = specialRequest
  if (connectedRoomOfferIds.includes(offer.id)) {
    specialRequestWithConnectedRoomsMessage = addConnectRoomsSpecialRequestMessage(
      specialRequest,
      connectedRoomContactNames?.length ? connectedRoomContactNames : getContactName(primaryTraveller, account),
    )
  }

  const data: any = {
    // common fields
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,
    guest_special_requests: specialRequestWithConnectedRoomsMessage,
    customer_email: primaryTraveller?.data.email || account.email,
    customer_name: account.fullName,
    arrival_details: arrivalDetailFields,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(cartMode === 'select-date' ? selectDateFields : newBookingFields),
  }

  if (item.reservationType === RESERVATION_TYPE_BOOK_LATER && item.occupancy) {
    data.intended_occupancy = {
      number_of_adults: item.occupancy.adults,
      number_of_children: item.occupancy.children ?? 0,
      number_of_infants: item.occupancy.infants ?? 0,
      number_of_teenagers: item.occupancy.teenagers ?? 0,
      children_ages: item.occupancy.childrenAge ?? [],
    }
  }

  if (item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
    data.check_in = moment.utc(item.checkIn).format(ISO_DATE_FORMAT)
    data.check_out = moment.utc(item.checkOut).format(ISO_DATE_FORMAT)
    data.number_of_adults = item.occupancy.adults
    data.number_of_children = item.occupancy.children
    data.number_of_infants = item.occupancy.infants
    data.number_of_teenagers = item.occupancy.teenagers
    data.children_ages = item.occupancy.childrenAge ?? []
    data.rate_plan_id = pkg.roomRate?.id
    data.is_upgraded_rate = item.isUpgradedRate

    if (primaryTraveller) {
      const guestNames = getRoomGuestNames({
        itemId: item.itemId,
        primaryForm: primaryTraveller,
        fallbackFirstName: primaryTraveller.data.firstName,
        fallbackLastName: primaryTraveller.data.lastName,
        isEnabled: config.agentHub.isEnabled && typeof roomGuestIndex === 'number' && isRoomGuestFieldsOffer(offer),
        isPrimaryRoom: roomGuestIndex === 0,
      })

      data.guest_first_name = guestNames.firstName
      data.guest_last_name = guestNames.lastName
      if (config.agentHub.isEnabled) {
        data.is_primary = isPrimaryTravellerName(guestNames.firstName, guestNames.lastName, primaryTraveller)
      }
      data.guest_phone = (primaryTraveller.data.phonePrefix && primaryTraveller.data.phone) ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined
    }

    if (OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type)) {
      const instantBookingPrice = instantBookingPricing?.price ?? 0
      // For channel-marked LPC, send the un-marked base rate so svc-reservation's
      // strict-equality check passes; svc-order charges the marked price + surcharge.
      const baseRoomRate = channelMarkupApplied
        ? (instantBookingPricing?.baseRoomRate ?? instantBookingPrice)
        : instantBookingPrice
      data.room_rate_amount = item.newPrice || baseRoomRate
    }
  }

  if (channelMarkup && !channelMarkup.channelMarkupBlacklist.includes(offer.id)) {
    const markupDetails = getChannelMarkupDetails(offer.id, offer.type, channelMarkup)
    if (markupDetails.isApplied) {
      data.channel_markup_id = channelMarkup.channelMarkupId
      data.channel_markup_round_to_ten = true
    }
  }
  // Include base package ID for loyalty upgrades for analytics and validation
  if (item.loyaltyAppliedUpgradeType && item.loyaltyBasePackageId) {
    data.base_package_id = item.loyaltyBasePackageId
    data.applied_upgrade_type = item.loyaltyAppliedUpgradeType
  }
  if (item.isAccomBundle) {
    data.is_accom_bundle = item.isAccomBundle
  }
  if (item.bypassRegionFilter) {
    data.bypass_region = true
    if (item.bypassRegionSettingId) {
      data.bypass_region_setting_id = item.bypassRegionSettingId
    }
  }
  return data
}

function formatLeTourV1Item(
  item: App.Checkout.LETourV1Item,
  form: App.Checkout.TravellerForm,
  specialRequest: string,
  offer: App.Offer,
  account: App.AuthAccount,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const pkg = offer.packages.find(pkg => pkg.id === item.packageId)
  if (!pkg) {
    throw new Error(`Package with id ${item.packageId} not found in offer ${offer.id}`)
  }
  const dob = form.data.dob || form.data.dateOfBirth
  const phone = (form.data.phonePrefix && form.data.phone) ? `${form.data.phonePrefix}${form.data.phone}` : undefined

  return {
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,

    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,

    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    value: pkg.value,
    price: pkg.price,
    localizedPrices: pkg.trackingPrice,
    number_of_days: pkg.duration,

    offer_booking_type: 'reservation',
    reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
    guest_special_requests: specialRequest,

    customer_email: account.email,
    customer_name: account.fullName,

    start_date: moment.utc(item.startDate).format(ISO_DATE_FORMAT),
    end_date: moment.utc(item.endDate).format(ISO_DATE_FORMAT),

    guest_first_name: form.data.firstName,
    guest_middle_name: form.data.middleName,
    guest_last_name: form.data.lastName,
    guest_title: form.data.title,
    guest_phone: phone,

    dob: moment.utc(dob).format(ISO_DATE_FORMAT),
    postcode: form.data.postcode,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)) ?? [],
  }
}

function formatTourExperienceItem(experienceItem: App.Checkout.TourV2ExperienceItem) {
  const count = sumUpOccupancies(experienceItem.occupants)

  return {
    id: experienceItem.purchasableOption.fkExperienceId,
    itinerary_id: experienceItem.purchasableOption.fkItineraryId,
    day_number: experienceItem.purchasableOption.day,
    duration: experienceItem.purchasableOption.duration,
    name: experienceItem.purchasableOption.name,
    description: experienceItem.purchasableOption.description,
    location: experienceItem.purchasableOption.location,
    price: experienceItem.purchasableOption.price,
    time_slot: experienceItem.purchasableOption.timeSlot,
    count,
    date: experienceItem.date,
    transaction_key: experienceItem.transactionKey,
  }
}

function formatTourV2ItemGroup(
  /** An array of items for a particular tour/option/departure */
  items: Array<App.Checkout.TourV2Item>,
  experienceItems: Array<App.Checkout.TourV2ExperienceItem>,
  specialRequests: { [itemId: string]: string },
  travellerForms: Array<App.Checkout.TravellerForm>,
  primary: App.Checkout.TravellerForm,
  offer: App.Tours.TourV2Offer,
  schema: App.Checkout.FormSchema,
  checkoutWithMemberPrice: boolean,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const occupants = schema.properties
  const occupantsRoomMap: Record<string, { index: number, unit: string }> = Object.entries(occupants)
    .reduce((acc, [key, value]) => {
      const relatedItem = value.relatedItem
      if (!relatedItem) { return acc }
      return { ...acc, [key]: { index: relatedItem.index, type: relatedItem.unit } }
    }, {})
  const travellerDetails = travellerForms.map((form, i) => {
    const formId = form.id
    const roomType = items[occupantsRoomMap[formId].index]?.purchasableOption.roomType
    const roomNumber = occupantsRoomMap[formId].index + 1
    const res = {
      first_name: form.data.firstName,
      middle_name: form.data.middleName ? form.data.middleName : undefined,
      last_name: form.data.lastName,
      email_address: form.data.email,
      title: form.data.title,
      gender: deduceGenderFromTitle(form.data.title as App.Title),
      type: form.id.split('-')[0],
      date_of_birth: form.data.dateOfBirth,
      is_lead_passenger: i === 0,
      room_type: roomType,
      room_number: roomNumber,
      phone_prefix: form.data.phonePrefix,
      phone: form.data.phone,
      departure_airport: form.data.departureAirportsList ?? undefined,
    }

    return res
  })

  const phone = `+${primary.data.phonePrefix}${primary.data.phone}`

  // non-TTC tours don't need address
  const requiresAddress = offer.productType === 'connection_tour'

  const address = {
    city: primary.data.city,
    country_code: primary.data.countryOfResidence,
    line1: primary.data.address,
    postcode: primary.data.auPostcodeField ?? primary.data.usPostcodeField ?? primary.data.postcode,
    region: primary.data.auStateField ?? primary.data.usStateField ?? primary.data.state,
  }

  const contactDetails = {
    phone,
    email: primary.data.email,
    ...(requiresAddress ? { address } : {}),
  }

  const price = sum(
    items.map(
      item => generateDepartureRoomPriceBreakdown(
        item.occupancy,
        item.purchasableOption,
        offer,
      ).totalPrice,
    ),
  )

  const memberPrice = sum(
    items.map(
      item => generateDepartureRoomPriceBreakdown(
        item.occupancy,
        item.purchasableOption,
        offer,
      ).totalMemberPrice,
    ),
  )

  const tourPrice = checkoutWithMemberPrice && memberPrice > 0 ? memberPrice : price
  const totalPrice = sum([tourPrice, ...experienceItems.map(item => item.total!)])

  const tourOptionId = offer.variations?.[items[0].purchasableOption.fkVariationId]?.fkTourOptionId
  const package_option = getPackageOptionType(items[0].purchasableOption.roomType)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'tourCruiseAndOptionalExperiences' } })
  return {
    type: 'tour',
    product_type: offer.productType,
    // Since the items are grouped by departure, these IDs are the same for the entire group. Can take them from the first item
    tour_id: items[0].offerId,
    fine_print: offer.finePrint?.sections,
    tour_option_id: tourOptionId,
    departure_id: items[0].purchasableOption.fkDepartureId,
    transaction_key: items[0].transactionKey,
    price: totalPrice,
    rooms: items.map(item => ({
      room_type: item.purchasableOption.roomType,
      adult_count: item.occupancy.adults,
      child_count: item.occupancy.childrenAge?.length ?? 0,
      child_ages: item.occupancy.childrenAge ?? [],
      special_requests: specialRequests[item.itemId],
      room_type_pricing_id: item.purchasableOption.fkRoomTypePricingId,
    })),
    traveller_details: travellerDetails,
    contact_details: contactDetails,
    package_option,
    optional_experiences: experienceItems.map(formatTourExperienceItem),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, items[0].transactionKey)),
  }
}

function formatTourV2ItemGroupDateChanges(
  items: Array<App.Checkout.TourV2Item>,
  offer: App.Tours.TourV2Offer,
  checkoutWithMemberPrice: boolean,
) {
  const itemsPricing = items.map(
    item => generateDepartureRoomPriceBreakdown(
      item.occupancy,
      item.purchasableOption,
      offer,
    ),
  )
  const price = sum(itemsPricing, item => item.totalPrice)
  const memberPrice = sum(itemsPricing, item => item.totalMemberPrice)
  const tourPrice = checkoutWithMemberPrice && memberPrice > 0 ? memberPrice : price

  const originalPrice = items[0].originalTotal ?? 0
  const diffPrice = tourPrice - originalPrice

  const package_option = getPackageOptionType(items[0].purchasableOption.roomType)

  return {
    departure_id: items[0].purchasableOption.fkDepartureId,
    transaction_key: items[0].transactionKey,
    price: tourPrice,
    difference_to_pay: diffPrice > 0 ? diffPrice : 0,
    package_option,
    rooms: items.map(item => ({
      room_type: item.purchasableOption.roomType,
      adult_count: item.occupancy.adults,
      child_count: item.occupancy.childrenAge?.length ?? 0,
      child_ages: item.occupancy.childrenAge ?? [],
      room_type_pricing_id: item.purchasableOption.fkRoomTypePricingId,
    })),
  }
}

function formatCarHireItem(
  item: App.Checkout.CarHireItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  arrivalFlightNumber: string | null,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const primaryDriver = travellerForms.find((form) => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const offer = item.offer

  const selectedRateOption = getCarHireSelectedRateOption(item)

  const totalPrice = selectedRateOption.payNowAmount + (item.selectedInsurance?.total || 0)
  const payOnPickUpAmount = selectedRateOption.payOnArrivalAmount + sum(item.selectedAddons, addon => addon.total ?? 0)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'car_hire' } })

  return {
    transaction_key: item.transactionKey,
    type: 'car_hire',
    offer,
    pickUpLocation: {
      code: offer.pickUp.id,
      date: moment.utc(`${item.pickUpDate}T${item.pickUpTime}`),
    },
    returnLocation: {
      code: offer.dropOff.id,
      date: moment.utc(`${item.dropOffDate}T${item.dropOffTime}`),
    },
    reference: {
      dateTime: selectedRateOption.dateTime,
      id: selectedRateOption.id,
      idContext: selectedRateOption.idContext,
      type: selectedRateOption.type,
      url: selectedRateOption.url,
    },
    vehicle: {
      code: offer.vehicle.code,
      model: offer.vehicle.model,
      image: offer.vehicle.image.url,
      transmissionType: offer.vehicle.transmission,
      baggageQuantity: offer.vehicle.luggage,
      passengerQuantity: offer.vehicle.seats,
      fuelType: offer.vehicle.fuelType,
      doorCount: offer.vehicle.doors,
      category: offer.vehicle.type,
      driveType: offer.vehicle.driveType,
      size: {
        code: offer.vehicle.sizeCode,
        description: offer.vehicle.type,
      },
      type: {
        code: offer.vehicle.typeCode,
        description: offer.vehicle.type,
      },
    },
    driver: {
      givenName: primaryDriver?.data.firstName,
      surname: primaryDriver?.data.lastName,
      email: primaryDriver?.data.email,
      phoneNumber: primaryDriver?.data.phone,
      phoneCode: primaryDriver?.data.phonePrefix,
      age: primaryDriver?.data.dateOfBirth ? getAge(primaryDriver.data.dateOfBirth, new Date(item.pickUpDate)) : undefined,
    },
    value: item.value,
    total: totalPrice,
    payOnPickUpAmount,
    originalPrice: selectedRateOption.originalPrice,
    qtyPassengers: 1, // TODO car hire remove mock
    availabilityUuid: offer.availabilityUuid,
    ...(arrivalFlightNumber && {
      arrivalDetails: {
        number: arrivalFlightNumber,
        transportationCode: TRANSPORTATION_CODE.AIRPLANE,
      },
    }),
    ...(item.selectedAddons && {
      specialEquipments: Object.values(item.selectedAddons),
    }),
    ...(item.selectedInsurance?.type === 'enhanced' && {
      insuranceReference: {
        id: item.selectedInsurance.id,
        amount: item.selectedInsurance.total,
        currencyCode: item.selectedInsurance.currency,
        url: item.selectedInsurance.url,
      },
    }),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(item.bundledOrderId && {
      bundledOrderId: item.bundledOrderId,
    }),
  }
}

function formatCruiseItem(
  itemView: App.Checkout.CruiseAccommodationItemView,
  travellerForms: Array<App.Checkout.TravellerForm>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  shouldEarnLoyaltyPoints: boolean,
  personTitles: Array<App.Cruises.BookingPersonTitles>,
  shouldUseCruiseMemberPrice: boolean,
) {
  const item = itemView.item
  const titleGenderMap = buildBookingTitleGenderMap(personTitles)
  const travellerDetails = travellerForms.map((form, i) => ({
    first_name: form.data.firstName,
    last_name: form.data.lastName,
    email: form.data.email,
    title: form.data.title,
    gender: form.data.title ? titleGenderMap[form.data.title] : undefined,
    type: form.id.split('-')[0],
    date_of_birth: form.data.dateOfBirth,
    is_lead_passenger: i === 0,
  }))

  const isMemberPriceAvailable = shouldUseCruiseMemberPrice && itemView.totals.memberPrice > 0

  /* These fields are used on svc-order to calculate the potential savings
   * https://github.com/lux-group/svc-order/blob/master/src/models/svc-membership/index.ts#L670
   */
  const publicPrice = isMemberPriceAvailable ? itemView.totals.price : undefined
  const memberPrice = isMemberPriceAvailable ? itemView.totals.memberPrice : undefined

  const total = isMemberPriceAvailable ? memberPrice : itemView.totals.price

  return {
    type: 'cruise',
    departure_id: item.departureId,
    departure_date: item.startDate,
    arrival_date: item.endDate,
    session_id: item.sessionId,
    booking_id: item.bookingId,
    transaction_key: item.transactionKey,
    total,
    public_price: publicPrice,
    member_price: memberPrice,
    traveller_details: travellerDetails,
    offer_name: item.offerName,
    offer_id: item.offerId,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    should_earn_lux_loyalty_points: shouldEarnLoyaltyPoints,
  }
}

function formatBedbankItem(
  itemId: string,
  item: App.Checkout.BedbankHotelItem,
  bedbankItems: Array<App.Checkout.BedbankHotelItem>,
  specialRequests: string,
  primaryForm: App.Checkout.TravellerForm | undefined,
  roomGuestItems: Array<RoomGuestItem>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  cartHasPropertyCollectItem: boolean,
  regionCode: string,
  offer: App.BedbankOffer,
  channelMarkup?: App.ChannelMarkup,
  existingOrderTraveller?: App.ExistingOrderBedbankTraveller,
  sessions?: Record<string, {
    data: App.BedbankEnquiry | null
    state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
  }>,
) {
  let travellerFormData = {
    bookingPhone: primaryForm?.data.phonePrefix && primaryForm?.data.phone && `+${primaryForm.data.phonePrefix}${primaryForm.data.phone}`,
    bookingEmail: primaryForm?.data.email,
    guestFirstName: primaryForm?.data.firstName,
    guestLastName: primaryForm?.data.lastName,
    postalCode: primaryForm?.data.auPostcodeField || primaryForm?.data.usPostcodeField || primaryForm?.data.postcode,
    stateProvinceCode: primaryForm?.data.auStateField || primaryForm?.data.usStateField || primaryForm?.data.state,
  }

  if (existingOrderTraveller) {
    travellerFormData = {
      ...travellerFormData,
      ...existingOrderTraveller,
    }
  }

  const formattedItem = {
    brand: config.BRAND,
    type: ITEM_TYPE_BEDBANK,
    cart_item_id: itemId,
    session_id: item.sessionId,
    bed_group_id: item.bedGroupId,
    property_id: item.offerId,
    room_type_id: item.roomId,
    room_rate_id: item.roomRateId,
    is_flight_bundle: item.isFlightBundle,
    transaction_key: item.transactionKey,
    booking_email: travellerFormData?.bookingEmail,
    booking_phone: travellerFormData.bookingPhone,
    rooms: bedbankItems.map((roomItem) => {
      const roomGuestIndex = roomGuestItems.findIndex(roomGuestItem => roomGuestItem.itemId === roomItem.itemId)
      const guestNames = getRoomGuestNames({
        itemId: roomItem.itemId,
        primaryForm,
        fallbackFirstName: travellerFormData.guestFirstName,
        fallbackLastName: travellerFormData.guestLastName,
        isEnabled: roomGuestIndex >= 0,
        isPrimaryRoom: roomGuestIndex === 0,
      })

      return {
        guest_first_name: guestNames.firstName,
        guest_last_name: guestNames.lastName,
        ...(config.agentHub.isEnabled && {
          is_primary: isPrimaryTravellerName(guestNames.firstName, guestNames.lastName, primaryForm),
        }),
        guest_special_requests: specialRequests,
        occupancy: generateOccupancyStringByRoom(roomItem.occupancy),
      }
    }),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    is_upgraded_rate: item.isUpgradedRate,
    is_accom_bundle: item.isAccomBundle,
    // Channel markup (LPP): tell svc-order to authoritatively re-apply the same markup it
    // charges, so the order total matches the marked-up price the customer was shown.
    // getChannelMarkupDetails already enforces the blacklist + LPP eligibility.
    ...(channelMarkup && getChannelMarkupDetails(offer.id, offer.type, channelMarkup).isApplied
      ? {
          channel_markup_id: channelMarkup.channelMarkupId,
          channel_markup_round_to_ten: true,
        }
      : {}),
  }

  if (cartHasPropertyCollectItem && item.roomRateId && item.sessionId && sessions?.[item.sessionId]?.data?.paymentMethod === 'propertyCollect') {
    return {
      ...formattedItem,
      billing_address: {
        country_code: regionCode,
        postal_code: travellerFormData.postalCode,
        state_province_code: travellerFormData.stateProvinceCode,
      },
    }
  }

  return formattedItem
}

function formatPassengers(
  travellerForms: Array<App.Checkout.TravellerForm>,
  flightItem: App.Checkout.FlightItem,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0 || !flightItem?.flights[0]) { return [] }
  const carrierCodes = flightItem.flights.map((f) => f.carrier)
  const paxList = flightItem.passengers

  const paxBaggageMap = flightItem.flights.map(flight => flight.extras.baggage)
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = paxList.map((pax: App.Checkout.FlightPassenger) => {
    const paxFormData = travellerForms.find(form => pax.id === form.id)
    if (!paxFormData) { return null }
    const paxSchema = schemaData?.accommodationFlightSchema.properties[pax.id]

    const allBaggage = paxBaggageMap.map(baggage => baggage[pax.id])
    const baggage = buildBaggagePayload(allBaggage[0], allBaggage[1])
    return buildPassengerPayload(pax, paxFormData, primaryForm, baggage, carrierCodes, paxSchema)
  }).filter(excludeNullOrUndefined)

  return result
}

function formatFlightItem(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isFlightChangeDates: boolean,
  isLuxPlusPriced: boolean,
): Array<unknown> {
  const isReturn = journeys.length === 2
  const isSameProvider = isReturn && new Set(journeys.map(journey => journey.provider)).size === 1

  if (isFlightChangeDates) {
    return journeys
  }

  if (item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN || item.viewType === FlightViewTypes.MULTI_CITY || isSameProvider) {
    return formatReturnFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData, isLuxPlusPriced)
  }

  return formatFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData, isLuxPlusPriced)
}

function formatFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isLuxPlusPriced: boolean,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = item.flights.map((flight, index) => {
    const segmentJourney = journeys[index]

    if (segmentJourney) {
      const seatEligiblePassengers = item.passengers.filter(passenger => passenger.type !== 'infant').length
      const selectedJourneyPrice = getEffectiveFlightPrice(segmentJourney, seatEligiblePassengers)
      const baggageOptions = baggageOptionsMap(segmentJourney.flightGroup.flights[0])?.baggage ?? []

      const cost = sum([
        flight.cost ?? selectedJourneyPrice.all.totalFare,
        sum(Object.values(item?.otherFees ?? [])),
        computeBaggageCostForPaxList(item.passengers, baggageOptions, flight.extras.baggage),
      ])

      const tax = selectedJourneyPrice.all.totalTax

      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

      return {
        provider: segmentJourney.provider,
        transaction_key: item.transactionKey,
        type: CHECKOUT_ITEM_TYPE_FLIGHT,
        isFnFBooking: item.isFnFBooking,
        offer_type: [(item.bundledItemIds?.length ?? 0) > 0 ? 'hotel' : null],
        contact_email_address: primaryTraveller?.data.email,
        contact_phone_number: primaryTraveller ? `${primaryTraveller?.data.phonePrefix}${primaryTraveller?.data.phone}` : undefined,
        passengers: formatPassengers(travellerForms, item, schemaData),
        journey: {
          id: flight.journeyId,
          cost,
          tax,
          searchId: item.searchId,
        },
        viewType: item.viewType,
        item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
        ...(flight.paymentScheduleSupported && { is_payment_schedule_supported: flight.paymentScheduleSupported }),
        is_lux_plus_priced: isLuxPlusPriced,
      }
    }
  }).filter(excludeNullOrUndefined)

  return result
}

function formatReturnFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isLuxPlusPriced: boolean,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const seatEligiblePassengers = item.passengers.filter(passenger => passenger.type !== 'infant').length

  const cost = sum([
    ...item.flights.map(flight => flight.cost),
    sum(Object.values(item?.otherFees ?? [])),
    ...journeys.map((journey, index) => {
      const baggageOptions = baggageOptionsMap(journey.flightGroup.flights[0])?.baggage ?? []
      return computeBaggageCostForPaxList(item.passengers, baggageOptions, item.flights[index]?.extras.baggage ?? {})
    }),
  ])

  const tax = sum(journeys.map(journey => getEffectiveFlightPrice(journey, seatEligiblePassengers).all.totalTax))

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

  // Check if any flight has paymentScheduleSupported for return flights
  const isPaymentScheduleSupported = item.flights.some(flight => flight.paymentScheduleSupported === true)

  const result = {
    provider: journeys[0].provider,
    transaction_key: item.transactionKey,
    type: CHECKOUT_ITEM_TYPE_FLIGHT,
    isFnFBooking: item.isFnFBooking,
    offer_type: [(item.bundledItemIds?.length ?? 0) ? 'hotel' : null],
    contact_email_address: primaryTraveller?.data.email,
    contact_phone_number: primaryTraveller ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined,
    passengers: formatPassengers(travellerForms, item, schemaData),
    journey: {
      id: journeys[0].id,
      cost,
      tax,
      searchId: item.searchId,
      selectedFareIds: item.flights.map(flight => flight.journeyId),
    },
    viewType: item.viewType === FlightViewTypes.MULTI_CITY ?
      FlightViewTypes.MULTI_CITY :
      FlightViewTypes.TWO_ONE_WAYS_AND_RETURN,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(isPaymentScheduleSupported && { is_payment_schedule_supported: true }),
    is_lux_plus_priced: isLuxPlusPriced,
  }

  return [result]
}

const getFormattedLeInstantBookingHotelItems = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getAccommodationAndExperienceItemDiscounts(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (
    items,
    accommodationItems,
    offers,
    calendarsRatesByOccupancy,
    availableRatesByOccupancy,
    travellerForms,
    specialRequests,
    account,
    cartMode,
    promoCodeItemDiscounts,
    checkoutWithMemberPrice,
    channelMarkup,
    connectedRoomOfferIds = [],
  ) => {
    const roomGuestItems = config.agentHub.isEnabled ? getRoomGuestItems(accommodationItems, offers) : EmptyArray
    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    let hasRequiredData = true
    const data = items
      .filter(isInstantBookingLEHotelItem)
      .map((item) => {
        const offer = offers[item.offerId]
        if (!offer) {
          hasRequiredData = false
          return null
        }

        const {
          data: instantBookingPricing,
          hasRequiredData: hasRequiredInstantBookingPricing,
        } = getInstantBookingPriceAndSurcharge(item, offer, calendarsRatesByOccupancy, availableRatesByOccupancy)

        const { itemDiscounts } = promoCodeItemDiscounts
        if (!hasRequiredInstantBookingPricing) {
          hasRequiredData = false
          return null
        }
        const specificItemDiscounts = filterItemDiscounts({ itemDiscounts, lookup: { specificType: 'accommodation' } })
        const roomGuestIndex = roomGuestItems.findIndex(roomGuestItem => roomGuestItem.itemId === item.itemId)
        const roomGuestItemsForOffer = roomGuestItems.filter(roomGuestItem =>
          roomGuestItem.offerId === item.offerId &&
          roomGuestItem.checkIn === item.checkIn &&
          roomGuestItem.checkOut === item.checkOut,
        )
        const connectedRoomContactNames = getRoomGuestContactNames({
          roomGuestItems: roomGuestItemsForOffer,
          primaryForm: primaryTraveller,
          fallbackFirstName: primaryTraveller?.data.firstName,
          fallbackLastName: primaryTraveller?.data.lastName,
        })

        return formatLeHotelItem(
          item,
          travellerForms,
          specialRequests[item.itemId],
          offer,
          instantBookingPricing,
          account,
          checkoutWithMemberPrice,
          connectedRoomOfferIds,
          cartMode,
          undefined,
          specificItemDiscounts,
          channelMarkup,
          roomGuestIndex >= 0 ? roomGuestIndex : undefined,
          connectedRoomContactNames,
        )
      })

    return { hasRequiredData, data }
  },
)

export const getFormattedVillaItems = createSelector(
  (state: App.State) => getVillaViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.arrivalDetails,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (
    views,
    travellerForms,
    specialRequests,
    account,
    checkoutWithMemberPrice,
    arrivalDetails,
    promoCodeItemDiscounts,
    channelMarkup,
    connectedRoomOfferIds = [],
  ) => {
    return views.data.map(view => {
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts
      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'accommodation', cartItemIds: [view.item.itemId] } })
      return formatLeHotelItem(
        view.item,
        travellerForms,
        specialRequests[view.item.itemId],
        view.offer,
        view.totals,
        account,
        checkoutWithMemberPrice,
        connectedRoomOfferIds,
        undefined,
        arrivalDetails[view.item.itemId],
        itemDiscounts,
        channelMarkup,
      )
    })
  },
)

export type FormattedOrderSubscriptionItem = Pick<Order.SubscriptionItem, 'subscription_offer_id' | 'type' | 'transaction_key' | 'total' | 'sub_type'> & {
  item_discounts?: Array<App.Checkout.FormattedItemDiscount>
  customer_phone?: string
  customer_phone_prefix?: string
}

export const getFormattedLuxPlusSubscriptionItems = createSelector(
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (state: App.State) => getSubReoccuringItemDiscounts(state),
  (items: Array<App.Checkout.LuxPlusSubscriptionItem>, subReoccuringItemDiscounts: GetPromoCodeItemDiscounts) => {
    if (!subReoccuringItemDiscounts.hasRequiredPromoCodeData) {
      return []
    }
    return items.map<FormattedOrderSubscriptionItem>(item => {
      return {
        subscription_offer_id: item.offerId,
        type: 'subscription',
        transaction_key: item.transactionKey,
        total: item.amount,
        sub_type: 'recurring_fee',
        item_discounts: subReoccuringItemDiscounts.itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
      }
    })
  },
)

export const getFormattedSubscriptionJoinFeeItems = createSelector(
  (state: App.State) => getSubscriptionJoinItems(state),
  (state: App.State) => getSubscriptionJoinDiscountItems(state),
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (state: App.State) => state.auth.account,
  (items, subJoinDiscounts, subscriptionItems, account): Array<FormattedOrderSubscriptionItem> => {
    // Get phone data from the main subscription item (should only be one for standalone checkout)
    const subscriptionItem = subscriptionItems[0]

    // We don't want to send the waived items to the backend
    return items.filter(item => !item.waived).map<FormattedOrderSubscriptionItem>(item => ({
      subscription_offer_id: item.subscriptionOfferId,
      type: 'subscription',
      transaction_key: item.transactionKey,
      total: item.amount,
      sub_type: 'joining_fee',
      item_discounts: subJoinDiscounts.itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
      customer_phone: subscriptionItem?.phone || account.phone,
      customer_phone_prefix: subscriptionItem?.phonePrefix || account.phonePrefix,
    }))
  },
)

export const getFormattedCartItems = createSelector(
  (state: App.State) => state.checkout.cart,
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.auth.account,
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (state: App.State) => getFormattedLeInstantBookingHotelItems(state),
  (state: App.State) => getFormattedTourV2Items(state),
  (state: App.State) => getBedbankItemIdToLeadItemId(state),
  (state: App.State) => getFormattedExperienceItems(state),
  (state: App.State) => getFormattedTransferItems(state),
  (state: App.State) => getFormattedInsuranceItems(state),
  (state: App.State) => getFormattedBookingProtectionItems(state),
  (state: App.State) => getFormattedCruiseItems(state),
  (state: App.State) => getFormattedBundleAndSaveItems(state),
  (state: App.State) => getFormattedGiftCardItems(state),
  (state: App.State) => getFormattedVillaItems(state),
  (state: App.State) => getFormattedLuxPlusSubscriptionItems(state),
  (state: App.State) => getFormattedSubscriptionJoinFeeItems(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getFormattedTourExperienceItems(state),
  (state: App.State) => getBedbankChangeDatesTravellers(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getFormattedLoyaltyLoungePassItems(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getFormattedMerchandiseItems(state),
  (state: App.State) => getFormattedLoyaltyPurchasePointsItems(state),
  (state: App.State) => getFormattedMerchandiseShippingItems(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.sessions,
  getIsLuxPlusPricedFlight,
  formatCartItems,
)

export const getIsCheckoutOrderPayloadReady = createSelector(
  (state: App.State) => getFormattedCartItems(state),
  (formattedCartItems): boolean => formattedCartItems.hasRequiredData,
)

export const getOrderGift = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (travellers): Order.OrderGift | undefined => {
    if (travellers.length < 2) { return undefined }
    const giftRecipient = travellers.find(traveller => traveller.id === GIFT_RECIPIENT_FORM_ID)
    if (giftRecipient) {
      const giftGiver = travellers.find(traveller => traveller.id === PRIMARY_TRAVELLER_FORM_ID)
      const giftPayload: Order.OrderGift = {
        receiver_email: giftRecipient.data.email!,
        receiver_name: giftRecipient ? `${giftRecipient.data.firstName} ${giftRecipient.data.lastName}` : 'undefined',
        giver_name: giftGiver ? `${giftGiver.data.firstName} ${giftGiver.data.lastName}` : '',
        scheduled_date: generateScheduleDate(giftRecipient.data.scheduledDate!),
      }
      if (giftRecipient.data.personalMessage) {
        giftPayload.gift_message = giftRecipient.data.personalMessage
      }
      return giftPayload
    }
  },
)
